import {
  memoActivePayloadSchema,
  MemoActivePayloadSchema,
  useCreateMemoActive,
  useFirstServcoID,
  useIntegrationMemoActives,
} from "@/api/generate-hpdb";
import { FieldCombobox } from "@/components/form/field-combobox";
import { FieldInput } from "@/components/form/field-input";
import { FieldInputDate } from "@/components/form/field-input-date";
import { FormContainer } from "@/components/form/form-container";
import { SubmitButton } from "@/components/ui/button";
import { VerticalGaps } from "@/components/ui/container";
import { MISSING_ID_IN_ROUTE } from "@/utils/constant";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useApprovers } from "@/api/master-param";
import { useEffect, useState } from "react";
import { swalError, swalSuccess } from "@/utils/lib/swal";
import { FieldTextarea } from "@/components/form/field-textarea";
import { useContracts } from "@/api/hpm";

export function MemoActiveForm({
  isReadOnly = false,
  onFormDataChange,
}: {
  isReadOnly: boolean;
  onFormDataChange: (hasData: boolean) => void;
}) {
  const [approverSearch, setApproverSearch] = useState("");
  const [firstServcoIdSearch, setFirstServcoIdSearch] = useState("");
  const [contractSearch, setContractSearch] = useState<string>("");

  const { id } = useParams();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  const { data: integrationMemoActives } = useIntegrationMemoActives(
    {
      rfsId: id,
    },
    {
      staleTime: Infinity,
    },
  );
  const integrationMemoActive = integrationMemoActives?.[0];

  const form = useForm<MemoActivePayloadSchema>({
    resolver: zodResolver(memoActivePayloadSchema),
    values: {
      dateLm: integrationMemoActive?.dateLm ?? "",
      footage: integrationMemoActive?.footage ?? "",
      remarks: integrationMemoActive?.remarks ?? "",
      rfs: {
        id: id,
        dateFeederDone: integrationMemoActive?.rfs?.dateFeederDone ?? "",
        dateOltDone: integrationMemoActive?.rfs?.dateOltDone ?? "",
      },
      userApprover: {
        id: integrationMemoActive?.userApprover?.id ?? "",
      },
      firstServcoDesc: integrationMemoActive?.firstServcoDesc ?? "",
      contractId: integrationMemoActive?.contractId ?? "",
    },
  });

  const firstServcoID = form.watch("firstServcoDesc");

  // Clean the servcoName parameter before sending to useContracts
  const cleanedServcoName = firstServcoID?.includes(" : ")
    ? firstServcoID.split(" : ")[1]
    : firstServcoID;

  const { data: approvers } = useApprovers({
    search: approverSearch,
  });
  const { data: firstServcoIds } = useFirstServcoID();
  const { data: contracts } = useContracts(
    { servcoName: cleanedServcoName, contractName: contractSearch },
    {
      enabled: Boolean(firstServcoID),
    },
  );

  const firstServcoIdsOptions =
    firstServcoIds?.data?.map((firstServcoId) => ({
      value: firstServcoId.Description,
      label: firstServcoId.Description,
    })) ?? [];
  const contractOptions =
    contracts?.map((item: { contractName: string }) => ({
      label: item.contractName,
      value: item.contractName,
    })) ?? [];

  useEffect(() => {
    const hasData = !!integrationMemoActive;
    onFormDataChange(hasData);
  }, [integrationMemoActive, onFormDataChange]);

  const approver = form.getValues("userApprover") as {
    id: string;
    value: string;
  };

  const { mutate, isPending } = useCreateMemoActive();

  const handleSubmit = (values: MemoActivePayloadSchema) => {
    mutate(
      {
        data: {
          dateLm: values.dateLm,
          footage: values.footage,
          remarks: values.remarks,
          rfs: {
            id: id,
            dateFeederDone: values.rfs.dateFeederDone,
            dateOltDone: values.rfs.dateOltDone,
          },
          userApprover: {
            id: values.userApprover.id,
          },
          firstServcoDesc: values.firstServcoDesc,
          contractId: values.contractId,
        },
      },
      {
        onSuccess: () => {
          swalSuccess.fire({
            text: "Success create data Memo Active",
          });
        },
        onError: (error) => {
          swalError.fire({
            text: error.message,
          });
        },
      },
    );
  };

  return (
    <FormContainer form={form} onSubmit={handleSubmit}>
      <div className="grid grid-cols-[repeat(auto-fit,_minmax(17rem,_1fr))] gap-4">
        <VerticalGaps>
          <FieldInputDate
            name="dateLm"
            label="Date LM"
            disabled={isReadOnly || Boolean(integrationMemoActive)}
            hasRequiredAsterisk
          />
          <FieldInput
            name="footage"
            label="Footage (M)"
            isDisabled={isReadOnly || Boolean(integrationMemoActive)}
            hasRequiredAsterisk
          />
          <FieldCombobox
            name="contractId"
            label="Contract ID"
            search={contractSearch}
            setSearch={setContractSearch}
            options={contractOptions}
            isDisabled={isReadOnly || Boolean(integrationMemoActive)}
            hasRequiredAsterisk
          />
        </VerticalGaps>

        <VerticalGaps>
          <FieldInputDate
            name="rfs.dateFeederDone"
            label="Date Feeder"
            disabled={isReadOnly || Boolean(integrationMemoActive)}
          />
          <FieldInputDate
            name="rfs.dateOltDone"
            disabled={isReadOnly || Boolean(integrationMemoActive)}
            label="Date OLT"
          />
          <FieldTextarea
            name="remarks"
            label="Remarks"
            isDisabled={isReadOnly || Boolean(integrationMemoActive)}
          />
        </VerticalGaps>

        <VerticalGaps>
          <FieldCombobox
            name="userApprover.id"
            label="Approver"
            search={approverSearch}
            setSearch={setApproverSearch}
            isDisabled={isReadOnly || Boolean(integrationMemoActive)}
            hasRequiredAsterisk
            options={(approvers?.content ?? (approver && [approver]) ?? []).map(
              (approver) => ({
                value: approver.id,
                label: approver.value,
              }),
            )}
          />
          <FieldCombobox
            name="firstServcoDesc"
            label="First Servco ID"
            search={firstServcoIdSearch}
            setSearch={setFirstServcoIdSearch}
            options={firstServcoIdsOptions}
            isDisabled={isReadOnly || Boolean(integrationMemoActive)}
            hasRequiredAsterisk
          />
        </VerticalGaps>
      </div>
      <SubmitButton
        loading={isPending}
        disabled={isReadOnly || Boolean(integrationMemoActive)}
        className="ml-auto"
      />
    </FormContainer>
  );
}
